// ---- collect every field id across all section shapes into initial values ----
export function buildInitialValues(form) {
    const values = {};

    const addField = (field) => {
        if (field?.id) values[field.id] = getFieldDefault(field);
    };

    (form?.sections || []).forEach((section) => {
        (section?.fields || []).forEach(addField);
        (section?.extraFields || []).forEach(addField);

        // provider-info sections keep their fields under rows[]
        (section?.rows || []).forEach((row) => {
            (row?.fields || []).forEach(addField);
        });

        // contacts sections keep sub-field descriptors on each contact row
        (section?.contacts || []).forEach((c) => {
            [c.name, c.email, c.phone].forEach((sf) => {
                if (sf?.id) values[sf.id] = "";
            });
            if (c.canContact?.id) values[c.canContact.id] = "No";
        });
    });

    return values;
}

// ---- synthesize a proper field descriptor for a contact sub-field ----
export function contactField(kind, sf) {
    if (!sf) return null;
    const control = kind === "canContact" ? "radioYN" : "text";
    return {
        id: sf.id,
        control,
        encompassId: sf.encompassId,
        encompassIdLast: sf.encompassIdLast // borrower / co-borrower name = first + last
    };
}

// ---- flatten every field descriptor that carries an encompassId ----
export function collectFields(form) {
    const list = [];

    (form?.sections || []).forEach((section) => {
        (section?.fields || []).forEach((f) => list.push(f));
        (section?.extraFields || []).forEach((f) => list.push(f));
        (section?.rows || []).forEach((row) => {
            (row?.fields || []).forEach((f) => list.push(f));
        });
        (section?.contacts || []).forEach((c) => {
            ["name", "email", "phone", "canContact"].forEach((k) => {
                const f = contactField(k, c[k]);
                if (f) list.push(f);
            });
        });
    });

    return list;
}

// ---- date helpers (shared shape with cuReview) ----
export const mmddyyyyToIso = (s) => {
    if (!s) return "";
    const str = String(s).trim();
    if (str === "//") return "";
    if (/^\d{4}-\d{2}-\d{2}$/.test(str)) return str;
    const m = str.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
    if (!m) return "";
    const [, mm, dd, yyyy] = m;
    return `${yyyy}-${mm}-${dd}`;
};

export const isoToMdy = (iso) => {
    if (!iso || !/^\d{4}-\d{2}-\d{2}$/.test(iso)) return iso || "";
    const [y, m, d] = iso.split("-");
    return `${Number(m)}/${Number(d)}/${y}`;
};

// Encompass custom fields (CX.*) of Date type require a date-only ISO value
// ('yyyy-MM-dd', no time, no timezone offset). Normalize whatever the UI holds
// (already 'yyyy-MM-dd' from <input type=date>, but strip any stray time part).
export const toIsoDateOnly = (raw) => {
    const s = String(raw || "").trim();
    const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
    return m ? `${m[1]}-${m[2]}-${m[3]}` : "";
};

// Encompass custom fields are prefixed with "CX."; standard and virtual
// fields (numeric ids, URLA.X*, VEND.X*, etc.) are not.
export const isCustomFieldId = (encompassId) =>
    String(encompassId || "").startsWith("CX.");

export const mdyHmToDatetimeLocal = (s) => {
    if (!s) return "";
    const str = String(s).trim();
    if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(str)) return str;
    const m = str.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})(?:\s+(\d{1,2}):(\d{2}))?/);
    if (!m) return "";
    const mm = String(m[1]).padStart(2, "0");
    const dd = String(m[2]).padStart(2, "0");
    const yyyy = m[3];
    const hh = String(m[4] ?? "00").padStart(2, "0");
    const min = String(m[5] ?? "00").padStart(2, "0");
    return `${yyyy}-${mm}-${dd}T${hh}:${min}`;
};

export const datetimeLocalToMdyHm = (s) => {
    if (!s) return "";
    const str = String(s).trim();
    const m = str.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/);
    if (!m) return str;
    const [, yyyy, mm, dd, hh, min] = m;
    const hour24 = Number(hh);
    const hour12 = hour24 % 12 || 12;
    const ampm = hour24 >= 12 ? "PM" : "AM";
    return `${mm}/${dd}/${yyyy} ${String(hour12).padStart(2, "0")}:${min} ${ampm}`;
};

// ---- encompass <-> ui normalization ----
// default value carried on the option marked default:true (dropdowns)
export const getFieldDefault = (field) =>
    field?.default ?? (field?.options || []).find((o) => o.default)?.value ?? "";

export const normalizeFromEncompass = (field, raw) => {
    const fallback = getFieldDefault(field);
    if (raw == null) return fallback;
    if (raw === "//") return fallback;
    const str = String(raw).trim();
    if (str === "") return fallback;

    if (field?.control === "date") return mmddyyyyToIso(str);
    if (field?.control === "datetime") return mdyHmToDatetimeLocal(str);
    if (field?.control === "checkbox") {
        const checkedValue = field.checkedValue ?? "Y";
        const uncheckedValue = field.uncheckedValue ?? "N";
        const s = str.toLowerCase();
        const isChecked =
            str === String(checkedValue).trim() ||
            s === "y" ||
            s === "true" ||
            s === "1" ||
            s === "yes";
        return isChecked ? checkedValue : uncheckedValue;
    }
    if (field?.control === "radioYN") {
        if (str === "Y") return "Yes";
        if (str === "N") return "No";
    }
    return str;
};

export const normalizeForEncompassWrite = (field, raw) => {
    if (raw == null) return "";
    let val = String(raw).trim();

    if (field?.control === "decimal") val = val.replace(/,/g, "");
    if (field?.control === "date") {
        // Custom date fields (CX.*) require ISO 'yyyy-MM-dd'; standard/virtual
        // fields use locale MDY.
        val = isCustomFieldId(field?.encompassId) ? toIsoDateOnly(val) : isoToMdy(val);
    }
    if (field?.control === "datetime") val = datetimeLocalToMdyHm(val);
    if (field?.control === "checkbox") {
        const checkedValue = field.checkedValue ?? "Y";
        const uncheckedValue = field.uncheckedValue ?? "N";
        return String(raw) === String(checkedValue) ? checkedValue : uncheckedValue;
    }
    if (field?.control === "radioYN") {
        if (val === "Yes") return "Y";
        if (val === "No") return "N";
    }
    return val === "" ? "" : val;
};

const ALWAYS_DISABLED_IDS = new Set([
    "app1_completedBy",
    "app1_completedDate",
    "app2_completedBy",
    "app2_completedDate"
]);

// ---- single control renderer (mirrors cuReview Control) ----
export function Control({
    field,
    value,
    onChange,
    sectionDisabled = false,
    pageReadOnly = false,
    fieldForceReadOnly = false
}) {
    const isAlwaysDisabled =
        field?.isUpdate === false ||
        field?.readonly === true ||
        ALWAYS_DISABLED_IDS.has(field?.id);

    const disabled =
        isAlwaysDisabled || fieldForceReadOnly || sectionDisabled || pageReadOnly;

    const baseCls = `form-input w-full text-sm border rounded p-1 ${disabled ? "field-disabled" : ""}`;

    if (field.control === "textarea") {
        return (
            <textarea
                id={field.encompassId}
                className="form-input w-full text-sm border rounded p-1 resize-none"
                style={{ height: field.height || "72px" }}
                value={value || ""}
                disabled={disabled}
                onChange={(e) => onChange(field.id, e.target.value)}
            />
        );
    }

    if (field.control === "dropdown") {
        const opts = (field.options || [])
            .map((o) => (typeof o === "string" ? { text: o, value: o } : o))
            .filter((o) => o.active !== false); // hide only explicitly inactive options

        return (
            <select
                id={field.encompassId}
                className={baseCls}
                value={value || ""}
                onChange={(e) => onChange(field.id, e.target.value)}
                disabled={disabled}
            >
                <option value="">{field.placeholder || ""}</option>
                {opts.map((o) => (
                    <option key={o.value} value={o.value}>
                        {o.text}
                    </option>
                ))}
            </select>
        );
    }

    if (field.control === "checkbox") {

        const checkedValue = field.checkedValue ?? "Y";
        const uncheckedValue = field.uncheckedValue ?? "N";

        return (
            <label className="inline-flex items-center gap-2 text-sm text-gray-700 select-none">
                <input
                    id={field.encompassId}
                    type="checkbox"
                    className="h-4 w-4"
                    disabled={disabled}
                    checked={value === checkedValue}
                    onChange={(e) => onChange(field.id, e.target.checked ? checkedValue : uncheckedValue)}
                />
                {field.inlineLabel && <span>{field.label}</span>}
            </label>
        );
    }

    if (field.control === "radioYN") {
        return (
            <div className="flex items-center gap-6">
                {["Yes", "No"].map((o) => (
                    <label key={o} className="flex items-center gap-1 text-sm">
                        <input
                            e-id={field.encompassId}
                            type="radio"
                            disabled={disabled}
                            checked={value === o}
                            onChange={() => onChange(field.id, o)}
                        />
                        {o}
                    </label>
                ))}
            </div>
        );
    }

    const type =
        field.control === "number" ? "number" :
            field.control === "date" ? "date" :
                field.control === "datetime" ? "datetime-local" :
                    "text";

    return (
        <input
            id={field.encompassId}
            type={type}
            className={baseCls}
            disabled={disabled}
            placeholder={field.placeholder || ""}
            value={value || ""}
            onChange={(e) => onChange(field.id, e.target.value)}
        />
    );
}

// ---- labeled field (label above control) ----
export function LabeledField({ field, value, onChange, pageReadOnly, fieldForceReadOnly }) {
    if (field.control === "checkbox" && field.inlineLabel) {
        return (
            <div className="flex items-end h-full pb-1">
                <Control
                    field={field}
                    value={value}
                    onChange={onChange}
                    pageReadOnly={pageReadOnly}
                    fieldForceReadOnly={fieldForceReadOnly}
                />
            </div>
        );
    }

    return (
        <div>
            <div className="form-label mb-1">
                {field.label}
                {field.required && <span className="text-red-500">*</span>}
            </div>
            <Control
                field={field}
                value={value}
                onChange={onChange}
                pageReadOnly={pageReadOnly}
                fieldForceReadOnly={fieldForceReadOnly}
            />
        </div>
    );
}

export async function syncHostLoanWorkspace() {
    try {
        const loanObj = await window.elli.script.getObject("loan");
        await loanObj.merge();
        await loanObj.calculate();
    } catch (e) {
        console.warn("Host loan merge/calc failed (workspace may stay stale until refresh):", e);
    }
}
